import React, { useCallback, useState } from 'react'
import {
  Button,
  Dialog,
  Grid,
  Typography,
  makeStyles, DialogTitle, Divider, DialogActions,
} from '@material-ui/core'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import useInitClient from 'hooks/useInitClient'

import UnlockCustomerAccountDialog from './UnlockCustomerAccountDialog'
import { useTheme } from '@material-ui/core/styles'

const UserLockStateDescription = ({
  onCancel,
  open,
  comment,
  lockState,
  lockedUntil,
  userId,
  onReload,
}) => {
  const theme = useTheme()
  const classes = useStyles()
  const [isUnlockCustomerAccountOpen, setIsUnlockCustomerAccountOpen] = useState(false)
  const initClient = useInitClient()

  const unlockAccount = useCallback(() => {
    initClient.customerAccounts
      .unlock({
        customerAccountId: userId,
      })
      .then((response) => {
        onReload()
        setIsUnlockCustomerAccountOpen(false)
      })
  }, [initClient.customerAccounts, onReload, userId])

  const lockStateLabel = {
    Unlocked: 'Entsperrt',
    Locked: 'Gesperrt',
    DeletionLocked: 'Löschgeschützt',
  }

  const lockStateColor = {
    Unlocked: theme.palette.success.main,
    Locked: theme.palette.error.main,
    DeletionLocked: theme.palette.error.main,
  }

  const handleClose = () => {
    onCancel()
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby='title'
      maxWidth='sm'
      fullWidth
    >
      <DialogTitle id='title' onClose={onCancel}>
        <Typography component='div' variant='h4'>Kontosperrstatus</Typography>
      </DialogTitle>
      <Grid container direction='column' spacing={2} className={classes.content}>
        <Grid item>
          <Grid container justify='space-between' spacing={1}>
            <Grid item>
              <Typography component='span' display='block' variant='subtitle1'>
                Kontosperrstatus:
              </Typography>
            </Grid>
            <Grid item>
              <Typography
                component='span'
                display='block'
                variant='subtitle1'
                style={{ color: lockStateColor[lockState] || 'inherit' }}
              >
                {lockStateLabel[lockState] || lockState}
              </Typography>
            </Grid>
          </Grid>
        </Grid>

        {lockState !== 'Unlocked' && (
          <Grid item className={clsx(classes.unlockButtonWrapper)}>
            <Button
              color='primary'
              fullWidth
              onClick={() => setIsUnlockCustomerAccountOpen(true)}
              variant='outlined'
            >
              Nutzer entsperren
            </Button>

            <UnlockCustomerAccountDialog
              onClose={() => setIsUnlockCustomerAccountOpen(false)}
              onConfirm={unlockAccount}
              open={isUnlockCustomerAccountOpen}
            />
          </Grid>
        )}

        {lockedUntil && lockState !== 'Unlocked' && (
          <Grid item>
            <Grid container justify='space-between' spacing={1}>
              <Grid item>
                <Typography component='span' display='block' variant='subtitle1'>
                  Gesperrt bis:
                </Typography>
              </Grid>
              <Grid item>
                <Typography component='span' display='block' variant='subtitle1'>
                  {Intl.DateTimeFormat('de-DE', {
                    year: 'numeric',
                    day: '2-digit',
                    month: '2-digit',
                  }).format(new Date(lockedUntil))}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
        )}

        {comment && (
          <Grid item>
            <Grid container justify='space-between' spacing={1}>
              <Grid item>
                <Typography component='span' display='block' variant='subtitle1'>
                  Kommentar:
                </Typography>
              </Grid>
              <Grid item>
                <Typography component='span' display='block' variant='subtitle1'>
                  {comment}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
        )}
      </Grid>
      <Divider variant='middle' />
      <DialogActions>
        <Button autoFocus onClick={onCancel} variant='contained' color='primary'>
          Schliessen
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const useStyles = makeStyles(theme => ({
  content: {
    padding: theme.spacing(3),
  },
  unlockButtonWrapper: {
    marginTop: theme.spacing(1),
  },
}))

UserLockStateDescription.propTypes = {
  /** Comment value from user object which is displayed */
  comment: PropTypes.string,
  /** callback when dialog is cancelled */
  onCancel: PropTypes.func,
  /** controls whether the dialog is shown */
  open: PropTypes.bool,
  /** current user lock state */
  lockState: PropTypes.string,
  /** date until user is locked */
  lockedUntil: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Date),
  ]),
  /** ID of the customer account */
  userId: PropTypes.string.isRequired,
  /** Function for reloading user details */
  onReload: PropTypes.func,
}

export default UserLockStateDescription
